import { money, titleize } from "../../lib/api";
import { ConfidenceBadge } from "./Badges";

const SCORES = [["urgency", "Urgency"], ["document_completeness", "Document completeness"], ["equity_confidence", "Equity confidence"],
  ["financial_sustainability", "Financial sustainability"], ["title_lien_complexity", "Title/lien complexity"], ["sale_readiness", "Sale readiness"]];
const LP = { LOW: "bg-green-100 text-green-800", MEDIUM: "bg-amber-100 text-amber-800", HIGH: "bg-orange-100 text-orange-800", IMMEDIATE: "bg-red-600 text-white" };

export const Scorecard = ({ sc }) => (
  <div data-testid="scorecard" className="rounded-2xl border border-slate-200 bg-white p-5">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="font-display text-lg font-bold">Homeowner Resolution Scorecard</h3>
      <span data-testid="legal-priority" className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${LP[sc.legal_review_priority]}`}>Legal review: {sc.legal_review_priority}</span>
    </div>
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      {SCORES.map(([k, l]) => (
        <div key={k} data-testid={`score-${k}`}>
          <div className="flex justify-between text-sm"><span className="font-medium text-slate-600">{l}</span><span className="font-mono font-bold">{sc[k] ?? "—"}/100</span></div>
          <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-slate-900 transition-[width] duration-700" style={{ width: `${sc[k] ?? 0}%` }} /></div>
        </div>
      ))}
    </div>
    <p className="mt-4 text-xs text-slate-500">Scores are preliminary organizing tools — not legal or financial determinations.</p>
  </div>
);

const Row = ({ l, v, strong }) => (
  <div className={`flex justify-between py-1.5 text-sm ${strong ? "border-t border-slate-200 font-bold" : ""}`}><span className="text-slate-600">{l}</span><span className="font-mono">{v}</span></div>
);

export const EquityCard = ({ eq, cf }) => (
  <div data-testid="equity-card" className="rounded-2xl border border-slate-200 bg-white p-5">
    <h3 className="font-display text-lg font-bold">Equity &amp; Budget <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase text-amber-800">Estimates</span></h3>
    <div className="mt-3">
      <Row l="Estimated property value" v={money(eq.value)} />
      <Row l="First mortgage" v={`− ${money(eq.first_mortgage)}`} />
      {!!eq.second_mortgage && <Row l="Second mortgage" v={`− ${money(eq.second_mortgage)}`} />}
      {!!eq.heloc && <Row l="HELOC" v={`− ${money(eq.heloc)}`} />}
      {!!eq.tax_liens && <Row l="Tax liens" v={`− ${money(eq.tax_liens)}`} />}
      {!!eq.property_taxes_owed && <Row l="Property taxes owed" v={`− ${money(eq.property_taxes_owed)}`} />}
      {!!eq.hoa_liens && <Row l="HOA/COA liens" v={`− ${money(eq.hoa_liens)}`} />}
      {!!eq.other_liens && <Row l="Other liens" v={`− ${money(eq.other_liens)}`} />}
      <Row strong l="Estimated gross equity" v={<span data-testid="gross-equity">{money(eq.gross)}</span>} />
      <Row l="Est. transaction costs (8%)" v={`− ${money(eq.transaction_costs)}`} />
      {!!eq.payoff_items && <Row l="Est. other payoff items" v={`− ${money(eq.payoff_items)}`} />}
      <Row strong l="Estimated net sale equity" v={<span data-testid="net-equity">{money(eq.net)}</span>} />
    </div>
    {cf.known && (
      <div className="mt-4 rounded-xl bg-slate-50 p-3">
        <Row l="Monthly income" v={money(cf.income)} /><Row l="Monthly expenses" v={money(cf.expenses)} />
        <Row strong l="Monthly surplus / deficit" v={<span data-testid="monthly-surplus" className={cf.surplus < 0 ? "text-red-700" : "text-emerald-700"}>{money(cf.surplus)}</span>} />
        <p className="mt-1 text-xs text-slate-500">This does not determine whether you can afford the property.</p>
      </div>
    )}
  </div>
);

export const VerificationList = ({ items }) => (
  <div data-testid="verification-list" className="rounded-2xl border border-slate-200 bg-white p-5">
    <h3 className="font-display text-lg font-bold">What this review relies on</h3>
    <div className="mt-3 divide-y divide-slate-100">
      {items.map((v) => (
        <div key={v.field} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
          <span className="text-slate-700">{v.label}: <b className="font-mono">{typeof v.value === "number" || /^\d+(\.\d+)?$/.test(v.value ?? "") ? money(v.value) : titleize(v.value ?? "—")}</b></span>
          <ConfidenceBadge status={v.status} testId={`verify-${v.field}`} />
        </div>
      ))}
      {!items.length && <p className="text-sm text-slate-500">Answer a few questions to see this.</p>}
    </div>
  </div>
);
